// The six scenarios. Each names its pack, the sims it can run on (candidates in order of
// preference), the presets the left panel offers, the timeline it plays until a sim supplies its
// own, and the one-line Methods text. A loaded sim's header replaces the timeline and the Methods,
// so the panel always names the model and data actually on screen.

import type { Mode, Orbit, Preset as CameraPreset, SectionPlane, Target } from '../viewport/api.ts';

export type ScenarioId = 'explore' | 'bloodflow' | 'stroke' | 'seizure' | 'neurons' | 'drugs';

export interface ScenePreset {
  id: string;
  name: string;
  /** One short line under the name. */
  hint: string;
  camera: CameraPreset;
  /** An exact camera instead of `camera`'s framing, for a site no structure frames (where a specimen came from). */
  orbit?: Orbit;
  /** Layer modes by system; systems not named keep the pack's default. */
  layers?: Record<string, Mode>;
  isolate?: Target;
  section?: SectionPlane[];
  select?: string;
  /** The electrode sites the view draws over the picture (spec §15, Electrodes); a kind not named keeps its switch. */
  electrodes?: Partial<Record<'eeg' | 'seeg', boolean>>;
  /** Structures the view names first, as design's screens pick each state's callouts; one out of sight
   *  is named at its centroid, its pill saying hidden and its leader dashed (Seizure's onset zone). */
  callouts?: string[];
  time?: number;
  /** The playback rate the view sets (scene seconds a second), one its timeline offers: Stroke's depolarizations at 1 min/s. */
  speed?: number;
  /** A connectome or tissue pack (connectome-pack-v0) the view loads instead of the scene's, such as H01's
   *  cortex. The scene's sims aren't drawn there, and a viewport without such packs isn't offered it. */
  pack?: string;
}

export interface TimelineEvent {
  t: number;
  label: string;
  /** Label priority where labels would collide: lower ranks keep theirs (default 0). An empty label is a tick alone. */
  rank?: number;
}

export interface Timeline {
  /** Seconds of scenario time: where the timeline ends. */
  duration: number;
  /** Where it starts, s, where the run's first frame is after 0 (a circuit recorded after its settling time); else 0. */
  start?: number;
  /** How the readout shows time. */
  unit: 'ms' | 's' | 'min' | 'h';
  /** Loops at the end (a cardiac cycle). */
  periodic?: boolean;
  events?: TimelineEvent[];
  /** Playback rates offered, in scenario seconds per wall second. */
  speeds: number[];
  /** A clip's own slow motion (cells-sim-v0 `time.pacing`), offered as the Paced speed: from each `t`, `rate`. */
  pacing?: { t: number; rate: number }[];
}

/** One sim slot of a scene: the viewport's `sim` slot, filled by the first candidate that exists. */
export interface SimSlotSpec {
  slot: string;
  /** Run directories under sims/, most preferred first. `{id}` takes the value of the scene's choice `id`. */
  candidates: string[];
  /** Methods heading for a secondary slot ('Flow', 'Before the clot'). */
  label?: string;
  /** Loaded for traces and Methods only; the viewport isn't asked to draw it. */
  appOnly?: boolean;
  /** Template values its runs were made for: the slot is empty unless the scene's match (Stroke's
   *  spreading depolarization, run for one clot). */
  when?: Record<string, string>;
}

export interface ChoiceOption {
  value: string;
  label: string;
  hint?: string;
  /** Template values this option fixes over the Stroke slice's: v5's one collateral grade, whatever
   *  the switch says (its good, intermediate and poor runs are identical, ba-blood #536). */
  sets?: Record<string, string>;
  /** Neurons' Model and Cells sections: one cell's run, or a microcircuit's (`circuit`) in a `condition`. */
  circuit?: string;
  condition?: string;
  /** The scene's tagline in its panel while this option plays, where the scene's own differs. */
  tagline?: string;
}

/** A scene parameter that picks between runs: a drug, a collateral grade, a treatment time. */
export interface SceneChoice {
  id: string;
  label: string;
  options?: ChoiceOption[];
  /** Options listed from the first of these directories with an index.json; a value is the run's path. With `options`, those name
   *  runs by their index id, in that order, under their own labels. */
  index?: string[];
  /** Index rows of these kinds only. */
  kinds?: string[];
  /**
   * Index runs that stand in for older ones, by id: where both are listed, the run takes the place
   * and name of the first it replaces, and the others leave the list.
   */
  supersede?: Record<string, string[]>;
  /** An option value (or, for an index choice, a run id). */
  default?: string;
  /** Its value comes from the Stroke slice (app/stroke.ts), not a control of its own; the options still
   *  say which runs exist. */
  fromSlice?: boolean;
  /** The scene's own sections draw its controls (Neurons' Model and Cells), not the Scenario rows. */
  own?: boolean;
}

export interface Scenario {
  id: ScenarioId;
  name: string;
  /** Keyboard shortcut digit. */
  key: string;
  tagline: string;
  pack: string;
  sims?: SimSlotSpec[];
  choices?: SceneChoice[];
  /** The vessel a flow trace shows until one is selected. */
  traceDefault?: string;
  /**
   * A solver fast enough to re-run while you drag (hemo-rt: cut a vessel anywhere along it, flows
   * recompute), under sims/; the clot the scene opens with; `clinical` for Stroke's collaterals,
   * thrombectomy and tissue-run controls (app/stroke.ts); and hemo's ISLES'24 comparison exports, most
   * preferred first, for Compare with ISLES'24.
   */
  live?: { solver: 'hemo-rt'; path: string; clot?: { vessel: string; s: number }; clinical?: boolean; isles?: string[]; islesModel?: string };
  /** The one-line Methods caption under the viewport: model · data. */
  caption: string;
  /** Methods text until a sim header supplies its own. */
  methods: string;
  /** What the producing lane asked Methods to keep about a run its header doesn't say, by run id; it follows the header's text. */
  notes?: Record<string, string>;
  presets: ScenePreset[];
  timeline?: Timeline;
  /** `live`: needs no sim (Explore); `preview` until one of its sims loads and is real. */
  status: 'live' | 'preview';
  /** Its left panel must fit without scrolling (spec §1, Explore): where it wouldn't, Views collapses to its active row (§5). */
  fit?: boolean;
  /** Systems the scene leaves out: not in its layer list, never drawn, never labelled. */
  omit?: string[];
  /** The only systems the scene lists, as design's screens list them; the others are left out as `omit` leaves them. */
  systems?: string[];
  /** The moment the scene opens at, in its own time (seconds): design's screens' default state. */
  start?: number;
}

/** Whether a scene leaves a system out: not in its layer list, never drawn, never labelled. */
export const leavesOut = (sc: Pick<Scenario, 'omit' | 'systems'>, system: string) => !!sc.omit?.includes(system) || (!!sc.systems && !sc.systems.includes(system));

/**
 * hemo's network and tissue model, for Blood flow and Stroke. v5 is current (#536): its network has
 * explicit pial links, and its tissue runs come out the same for every collateral grade (no spatial
 * collateral model yet), so the scene takes the intermediate one and the panel says the grade is
 * inactive. v4 stays selectable as the previous model. Runs are never rewritten (#270); v2 and v3
 * carry openBF's taper bug (#411), so they aren't offered.
 */
const HEMO_MODEL: SceneChoice = {
  id: 'model',
  label: 'Model',
  default: 'v5',
  options: [
    { value: 'v5', label: 'v5', sets: { grade: 'intermediate' } },
    { value: 'v4', label: 'Previous', hint: 'v4, the previous model' },
  ],
};

/** IXI 165, the person behind Blood flow and Stroke: hemo's network is that subject's own vessels (decision 0006 §4). The scenes
 *  load this person's own cortex (FastSurfer's surfaces, with maps from fsaverage) composed over ixi-165-v0: the engine draws it in
 *  place of the parcel meshes and plays fsaverage runs on their folds (engines/wgpu #541). */
const SUBJECT = 'ixi-165-cortex-hd';
/** The spreading depolarization for hemo's proximal left M1 clot, driven by each model's tissue state at occlusion: fsaverage,
 *  0 to 30 min after it. ba-blood ran v5's (#582, header fe787b4d), dynamics v4's. */
const SD_V5 = 'spreading-depolarization-ixi165-m1-v5-841dcfd0-t0-v1';
const SD_V4 = 'spreading-depolarization-ixi165-m1-v4-8563700d-t0-v1';
/** The MNI152 2009c template, for Explore and for runs on template anatomy (Seizure's are fsaverage): the engine draws BigBrain's HD
 *  cortex over v0's cortex meshes (decision 0006; the template's own HD cortex where a node lacks BigBrain's), and plays surface runs
 *  on it through its maps. */
const TEMPLATE = 'mni152-2009c-v0';
/** Activity is on the brain: the template's vessels, sinuses and nerves would only cover it. */
const BRAIN_ONLY: Record<string, Mode> = { artery: 'hidden', vein: 'hidden', sinus: 'hidden', 'cranial-nerve': 'hidden' };
const SEIZURE_LAYERS: Record<string, Mode> = { subcortical: 'hidden', artery: 'shown' };

export const SCENARIOS: Scenario[] = [
  {
    id: 'explore',
    name: 'Explore',
    key: '1',
    tagline: 'A reference brain: BigBrain’s cortex, the template’s structures',
    // An MNI scene (decision 0006 §4, clarified 2026-10-02): BigBrain's cortex over v0's 836 structures.
    pack: TEMPLATE,
    caption: 'BigBrain cortex (Amunts 2013) in MNI 2009c · DKT parcels (Lewis 2020) · template nuclei · atlas arteries',
    methods:
      'Cortex: BigBrain, one person’s brain rebuilt from 7,404 histological sections at 20 µm (Amunts et al. 2013), placed in MNI152 2009c by an affine, with DKT parcels per vertex (Lewis et al. 2020). Around it, the MNI152 2009c template’s: FastSurfer’s deep nuclei, cerebellum and brainstem, Julich-Brain v3.1 areas, HCP-1065 tractography (Yeh 2022), arteries from the Mouches and Forkert atlas (2019) and Z-Anatomy’s cranial nerves.',
    status: 'live',
    fit: true,
    // Design's Explore lists nine systems (screens README, #548): v0's veins and dural sinuses would only cover the cortex.
    omit: ['vein', 'sinus'],
    presets: [
      { id: 'whole', name: 'Whole brain', hint: 'Cortex, deep nuclei and cerebellum', camera: 'hero' },
      { id: 'deep', name: 'Deep nuclei', hint: 'Basal ganglia, thalamus, hippocampus', camera: 'hero', isolate: { system: 'subcortical' } },
      { id: 'tracts', name: 'White-matter tracts', hint: 'Tractography bundles', camera: 'left', isolate: { system: 'tract' } },
      { id: 'arteries', name: 'Arteries', hint: 'Circle of Willis and branches', camera: 'inferior', isolate: { system: 'artery' } },
      { id: 'nerves', name: 'Cranial nerves', hint: 'From the brainstem outwards', camera: 'inferior', isolate: { system: 'cranial-nerve' } },
      // As design's explore-section-axial plate: the hero camera over the far side of z = +10 mm.
      { id: 'section', name: 'Axial section', hint: 'MRI slice through the deep nuclei', camera: 'hero', section: [{ axis: 'axial', mm: 10, keep: 'negative' }] },
    ],
  },
  {
    id: 'bloodflow',
    name: 'Blood flow',
    key: '2',
    tagline: 'Pulsatile flow through the Circle of Willis',
    // Stroke's pack too, so switching between the two loads nothing (about 4 s each way on the Mac otherwise).
    pack: SUBJECT,
    choices: [HEMO_MODEL],
    sims: [{ slot: 'main', candidates: ['hemo/ixi165-1d-{model}-baseline', 'hemo/ixi165-synth1d-baseline'] }],
    traceDefault: 'ba:left-mca-m1',
    live: { solver: 'hemo-rt', path: 'hemo/hemo-rt-axial-v0.2' },
    caption: '1D pulsatile flow · Circle of Willis from IXI 165',
    methods: '1D pulsatile blood flow on the cerebral arteries (hemo lane). The sim is on its way; anatomy is this person’s.',
    status: 'preview',
    // Design's Blood flow screens: the cortex and cerebellum around the arteries, at 300 ms of the cycle.
    systems: ['cortex', 'cerebellum', 'artery'],
    start: 0.3,
    timeline: { duration: 1.1, unit: 'ms', periodic: true, speeds: [0.1, 0.25, 0.5, 1], events: [{ t: 0.12, label: 'Systolic peak' }, { t: 0.42, label: 'Dicrotic notch' }] },
    presets: [
      { id: 'cow', name: 'Circle of Willis', hint: 'From below, the brain ghosted', camera: 'inferior', isolate: { system: 'artery' } },
      { id: 'mca', name: 'Middle cerebral arteries', hint: 'Both MCAs from the front', camera: 'anterior', isolate: { system: 'artery' } },
      { id: 'context', name: 'In the brain', hint: 'Arteries inside the cortex', camera: 'hero', layers: { cortex: 'ghost', artery: 'shown' } },
    ],
  },
  {
    id: 'stroke',
    name: 'Stroke',
    key: '3',
    tagline: 'A clot in the left M1, collaterals, penumbra and reperfusion',
    pack: SUBJECT,
    choices: [
      HEMO_MODEL,
      {
        id: 'grade',
        label: 'Collaterals',
        default: 'good',
        fromSlice: true,
        options: [
          { value: 'good', label: 'Good' },
          { value: 'intermediate', label: 'Fair', hint: 'Intermediate collateral grade' },
          { value: 'poor', label: 'Poor' },
        ],
      },
      {
        id: 'treatment',
        label: 'Thrombectomy',
        default: 'untreated',
        fromSlice: true,
        options: [
          { value: 'untreated', label: 'None', hint: 'Untreated' },
          { value: 'reperf-1h', label: '1 h', hint: 'Reperfusion at 1 hour' },
          { value: 'reperf-3h', label: '3 h', hint: 'Reperfusion at 3 hours' },
          { value: 'reperf-6h', label: '6 h', hint: 'Reperfusion at 6 hours' },
        ],
      },
    ],
    sims: [
      {
        slot: 'main',
        // {site} is the clot's site (app/stroke.ts): no run for a site hemo didn't simulate. Each
        // model's tissue runs were driven by its own flows beside them; the earlier (#242) runs by
        // v2's, which has openBF's taper bug, so they aren't fallbacks (#411).
        // {part} is '-distal' for an M1 clot beyond the lenticulostriate origin (app/stroke.ts).
        candidates: ['hemo/ixi165-stroke-{model}-{site}-{grade}-{treatment}{part}', 'hemo/ixi165-1d-{model}-occl-{site}{part}', 'hemo/ixi165-synth1d-occl-{site}{part}'],
      },
      { slot: 'flow', label: 'Flow', candidates: ['hemo/ixi165-1d-{model}-occl-{site}{part}', 'hemo/ixi165-synth1d-occl-{site}{part}'] },
      { slot: 'reference', label: 'Before the clot', appOnly: true, candidates: ['hemo/ixi165-1d-{model}-baseline', 'hemo/ixi165-synth1d-baseline'] },
      // On IXI 165's own cortex through its map from fsaverage, for the proximal clot at any grade and
      // arm, driven by the model on screen: v5's own run (v4's behind it where a node lacks v5's, its
      // penumbra overlapping v5's at Dice 0.94, notes), and the previous model's.
      { slot: 'sd', label: 'Spreading depolarization', candidates: [`dynamics/${SD_V5}`, `dynamics/${SD_V4}`], when: { model: 'v5', site: 'l-m1', part: '' } },
      { slot: 'sd', label: 'Spreading depolarization', candidates: [`dynamics/${SD_V4}`], when: { model: 'v4', site: 'l-m1', part: '' } },
    ],
    // Measured by ba-app on 10-02 from the runs' tissue-state at t = 0 (penumbra within the brain mask); a random mask of the
    // same size scores 0.21.
    notes: {
      [SD_V5]:
        'Driven by the v5 run on screen as it was at occlusion: hemo v5’s proximal left M1 tissue state (network 841dcfd0), where every v5 grade and arm starts. The model ran the first 30 minutes after occlusion, so the layer ends there.',
      [SD_V4]:
        'Driven by hemo v4’s proximal left M1 run with intermediate collaterals, as it was at occlusion. That run’s penumbra overlaps v5’s at Dice 0.94 (every v5 grade is the same), v4’s poor-collateral run’s at 0.94 and its good-collateral run’s at 0.83. The model ran the first 30 minutes after occlusion, so the layer ends there.',
    },
    traceDefault: 'ba:left-mca-m1',
    // The clot opens where hemo's proximal M1 clot is, 18 % along it (app/stroke.ts M1_CLOTS).
    live: { solver: 'hemo-rt', path: 'hemo/hemo-rt-axial-v0.2', clot: { vessel: 'ba:left-mca-m1', s: 0.18 }, clinical: true, isles: ['hemo/isles24-model-m1-ica-v5', 'hemo/isles24-model-m1-ica-v2'], islesModel: 'v4' },
    caption: 'Left M1 occlusion · 1D flow and tissue fate · IXI 165',
    methods: 'Left MCA M1 occlusion: 1D flow redistribution, perfusion deficit and tissue fate (hemo lane). The sim is on its way.',
    status: 'preview',
    // Design's Stroke screens list the cortex the deficit is on and the arteries the clot is in.
    systems: ['cortex', 'artery'],
    timeline: {
      duration: 6 * 3600,
      unit: 'h',
      speeds: [60, 600, 1800, 3600],
      events: [
        { t: 0, label: 'Occlusion' },
        { t: 3 * 3600, label: 'Thrombectomy' },
      ],
    },
    presets: [
      { id: 'occlusion', name: 'Occlusion', hint: 'Left M1, from the front', camera: 'anterior', isolate: { system: 'artery' } },
      { id: 'territory', name: 'MCA territory', hint: 'The cortex the left MCA feeds', camera: 'left', layers: { cortex: 'shown', artery: 'shown' } },
      { id: 'slice', name: 'Perfusion slice', hint: 'Axial slice through the deficit', camera: 'superior', section: [{ axis: 'axial', mm: 20, keep: 'negative' }] },
      // As ba-engine's #541 frame: the waves across the left hemisphere at 20 min.
      { id: 'sd', name: 'Depolarizations', hint: 'Waves across the penumbra at 20 min', camera: 'left', layers: { cortex: 'shown', artery: 'shown' }, time: 1200, speed: 60 },
    ],
  },
  {
    id: 'seizure',
    name: 'Seizure',
    key: '4',
    tagline: 'A left mesial temporal onset that recruits the network',
    pack: TEMPLATE,
    // The model alone (its scalp EEG and depth SEEG in volts, from distributed cortical sources), or a
    // real patient's recorded seizure beside the model fitted to it (the HUP fits).
    choices: [
      {
        id: 'run',
        label: 'Real patient',
        index: ['dynamics'],
        default: 'seizure-left-mesial-temporal-lateral-spread-distributed-v1',
        options: [
          { value: 'seizure-left-mesial-temporal-lateral-spread-distributed-v1', label: 'None', hint: 'The model alone: a left mesial temporal onset that spreads across the temporal lobe, with scalp EEG and depth SEEG' },
          { value: 'hup-seizure-sub-hup185', label: 'HUP185', hint: 'Their recorded seizure beside the model fitted to it: SEEG, 103 contacts' },
          { value: 'hup-seizure-sub-hup070', label: 'HUP070', hint: 'Their recorded seizure beside the model fitted to it: ECoG grid, 54 contacts' },
        ],
      },
    ],
    sims: [{ slot: 'main', candidates: ['{run}', 'dynamics/seizure-left-mesial-temporal-lateral-spread-distributed-v1', 'dynamics/seizure-left-mesial-temporal-v1', 'dynamics/seizure-left-mesial-temporal'] }],
    caption: 'Epileptor network · structural connectome · MNI152 2009c',
    methods: 'Epileptor network model on a structural connectome (dynamics lane), on the MNI152 2009c template brain.',
    // Design's Seizure screens: the cortex, the deep onset zone and the arteries; the cerebellum is in
    // their picture (its arteries would hang in the air without it), though not in their Layers.
    systems: ['cortex', 'subcortical', 'cerebellum', 'artery'],
    // Dynamics' note on the sensor amplitudes (#2, 2026-10-01): the source density is set in advance, not fitted.
    notes: { 'seizure-left-mesial-temporal-lateral-spread-distributed-v1': 'The scalp amplitude hasn’t been fitted to a reference recording.' },
    status: 'preview',
    timeline: { duration: 60, unit: 's', speeds: [0.25, 0.5, 1, 2, 4], events: [{ t: 10, label: 'Onset' }] },
    presets: [
      // Design's three moments of the model run (design/system/screens/seizure.html): the mesial onset
      // (3.1 s) just after it starts, and the front crossing the temporal lobe (12 parcels at 16.6 s,
      // its widest). Design's third, both hemispheres at 27 s, is extrapolated past the run: the run
      // stays in the left hemisphere and reaches the orbitofrontal cortex and cingulate by 22 s, so
      // Spread shows that, from below.
      // Each draws the cortex and its arteries with the deep nuclei hidden, as design's Layers do; the
      // onset zone is named through the cortex.
      { id: 'onset', name: 'Onset', hint: 'The left hippocampus, from the side', camera: 'left', layers: SEIZURE_LAYERS, callouts: ['ba:left-hippocampus'], electrodes: { eeg: false, seeg: false }, time: 4 },
      // With its 10–20 sites on: T7 lights over the temporal lobe as the front crosses it.
      { id: 'recruitment', name: 'Recruitment', hint: 'The front across the temporal lobe', camera: 'left', layers: SEIZURE_LAYERS, electrodes: { eeg: true }, time: 16.6 },
      {
        id: 'spread',
        name: 'Spread',
        hint: 'Into the orbitofrontal cortex, from below',
        camera: 'inferior',
        layers: SEIZURE_LAYERS,
        callouts: ['ba:left-lateral-orbitofrontal', 'ba:left-medial-orbitofrontal'],
        electrodes: { eeg: false },
        time: 22,
      },
    ],
  },
  {
    id: 'neurons',
    name: 'Neurons',
    key: '5',
    tagline: 'A human cortical neuron firing on its own reconstructed arbor',
    // The template brain in MNI, where the cells lane places its cells: the run's placement fits as it is.
    // The engine draws BigBrain's HD cortex over it (decision 0006).
    pack: 'mni152-2009c-v0',
    // Spec §15's Cells and Model sections pick the run: one cell on its own arbor, or a human microcircuit
    // (the brief's "a microcircuit fires") in its healthy or depressed state.
    choices: [
      {
        id: 'run',
        label: 'Model',
        index: ['cells'],
        own: true,
        default: 'eyal2018-0603-11-bap-nmda-r2',
        options: [
          { value: 'eyal2018-0603-11-bap-nmda-r2', label: 'Eyal 0603_11', hint: 'Its own reconstruction, from human temporal cortex' },
          { value: 'h01-4873639924-bap-nmda-r2', label: 'H01 4873639924', hint: 'The same model on an arbor traced in H01’s electron micrographs', tagline: 'A human neuron model firing on an arbor traced in H01' },
          { value: 'yao2022-l23net-baseline', label: 'L2/3', hint: 'Yao 2022’s human L2/3 microcircuit: 1,000 cells', circuit: 'L2/3', condition: 'Healthy', tagline: 'A human L2/3 microcircuit of 1,000 cells firing' },
          { value: 'yao2022-l23net-depression', label: 'L2/3, depression', hint: 'The same circuit in depression, as Yao models it: SST cells’ inhibition 40 % weaker', circuit: 'L2/3', condition: 'Depression', tagline: 'The same L2/3 circuit in depression, its SST inhibition weakened' },
          { value: 'guetmccreight2022-l5net-test100-r2', label: 'L5', hint: 'Guet-McCreight 2022’s human L5 microcircuit: 100 cells', circuit: 'L5', condition: 'Healthy', tagline: 'A human L5 microcircuit of 100 cells firing' },
        ],
      },
    ],
    // r2 supersedes the first run with the same data, adding probes and a pacing curve; the first is there for nodes without it.
    sims: [{ slot: 'main', candidates: ['{run}', 'cells/eyal2018-0603-11-bap-nmda-r2', 'cells/eyal2018-0603-11-bap-nmda'] }],
    caption: 'Eyal 2018 human L2/3 pyramidal cell · NEURON',
    methods: 'One human L2/3 pyramidal neuron from temporal cortex (Eyal et al. 2018 model), simulated in NEURON on its own reconstructed morphology (cells lane).',
    // ba-activity's Methods line for H01's v2 playback, the run Its tissue plays (#2, 2026-10-02).
    notes: { 'h01-lif-resting-recurrent-seed731-v2': 'Suprathreshold ALN Poisson volleys through assumed LIF on a sparse segmentation-derived subgraph.' },
    status: 'preview',
    // Until the run loads; its header then gives the clock. The last speed is the default: 20 ms of the cell's time a second.
    timeline: { duration: 1.15, unit: 'ms', speeds: [0.005, 0.01, 0.02] },
    presets: [
      {
        id: 'origin',
        name: 'Where it lives',
        hint: 'Left anterior middle temporal gyrus, from the left',
        camera: 'left',
        // Centred on the H01 site (h01-patch-v0's placement: the pia over the left anterior MTG, MNI −65.4, −4.7, −18.7),
        // where the cells lane's column runs sit; this run's cell is 22 mm behind it on the gyrus's crown.
        orbit: { targetMm: [-65.4, -4.7, -18.7], yawDeg: -90, pitchDeg: 0, distanceMm: 190 },
        // The lobe's DKT gyri: its Julich-Brain areas overlap them, and their labels would too.
        isolate: {
          ids: [
            'ba:left-superior-temporal',
            'ba:left-banks-sts',
            'ba:left-middle-temporal',
            'ba:left-inferior-temporal',
            'ba:left-temporal-pole',
            'ba:left-transverse-temporal',
            'ba:left-fusiform',
            'ba:left-parahippocampal',
            'ba:left-entorhinal',
          ],
        },
        select: 'ba:left-middle-temporal',
      },
      { id: 'brain', name: 'In the brain', hint: 'The whole brain from the left, the gyrus outlined', camera: 'left', select: 'ba:left-middle-temporal' },
      {
        id: 'tissue',
        name: 'Its tissue',
        hint: 'H01, a cubic millimetre of this gyrus: every cell, layer and vessel',
        camera: 'anterior',
        pack: 'h01-patch-v0',
        // From the front of the slab (pack axes follow the cortex, +z to the pia): the column of
        // column.json, x −0.248 mm, pia to white matter (z 1.42 to −1.14), with its centre cell picked out.
        orbit: { targetMm: [-0.248, 0, 0.14], yawDeg: 0, pitchDeg: 0, distanceMm: 5.2 },
        select: 'neuron:26724',
      },
    ],
  },
  {
    id: 'drugs',
    name: 'Drugs',
    key: '6',
    tagline: 'Dose to blood to brain to receptor',
    // Pharma's runs are on fsLR: the HD cortex drawn over the template (BigBrain's) has a map from it, a subject pack doesn't.
    pack: TEMPLATE,
    choices: [
      {
        id: 'drug',
        label: 'Drug',
        index: ['pharma', 'pharma/run-20261001-v0'],
        kinds: ['engagement', 'pk-course', 'comparison'],
        // LSD's dose-to-cortex course stands in for its snapshot and its series-only PK run.
        supersede: { 'lsd-200ug-cortex-v1': ['lsd', 'lsd-200ug-pk'] },
        default: 'lsd-200ug-cortex-v1',
      },
    ],
    sims: [{ slot: 'main', candidates: ['{drug}'] }],
    caption: 'Oral dose · plasma PK · receptor binding · PET receptor density',
    methods: 'Dose to blood to brain: plasma pharmacokinetics, equilibrium receptor binding and PET receptor density on the cortex (pharma lane).',
    status: 'preview',
    // Design's Drugs screens: 1:30 h after the dose, the engagement near its peak, on the brain alone.
    systems: ['cortex', 'cerebellum', 'brainstem'],
    start: 1.5 * 3600,
    timeline: { duration: 24 * 3600, unit: 'h', speeds: [600, 1800, 3600, 7200], events: [{ t: 0, label: 'Dose' }] },
    presets: [
      { id: 'whole', name: 'Whole brain', hint: 'The cortex from the front left', camera: 'hero', layers: BRAIN_ONLY },
      { id: 'left', name: 'Left hemisphere', hint: 'The lateral cortex', camera: 'left', layers: BRAIN_ONLY },
      { id: 'top', name: 'From above', hint: 'Both hemispheres', camera: 'superior', layers: BRAIN_ONLY },
      { id: 'back', name: 'Visual cortex', hint: 'The occipital lobes from behind', camera: 'posterior', layers: BRAIN_ONLY },
    ],
  },
];

export const scenarioById = (id: string): Scenario => SCENARIOS.find((s) => s.id === id) ?? SCENARIOS[0]!;

/** The structures the scene's current view names first (ScenePreset.callouts). */
export const viewCallouts = (s: { scenario: string; preset: string | null }): readonly string[] => scenarioById(s.scenario).presets.find((p) => p.id === s.preset)?.callouts ?? [];
